import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { FileDown, Search, RefreshCw, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  Cell, LabelList,
} from "recharts";

const DIMENSIONS = [
  { id: "design", label: "Design", plural: "Designs", accent: "bg-[#1A4DFF]", chartFill: "#1A4DFF", chartAccent: "#0A2FA6" },
  { id: "size",   label: "Size",   plural: "Sizes",   accent: "bg-black",     chartFill: "#0A0A0A", chartAccent: "#3A3A3A" },
  { id: "color",  label: "Color",  plural: "Colors",  accent: "bg-[#FF7A00]", chartFill: "#FF7A00", chartAccent: "#C05A00" },
];
const RANGES = [
  { id: "all",       label: "All Time" },
  { id: "this-year", label: "This Year" },
  { id: "this-month",label: "This Month" },
  { id: "custom",    label: "Custom" },
];
const STATUSES = ["ALL", "SALE", "REFUND", "EXCHANGE", "HIGH_RISK", "COD_RETURN"];

const todayIST = () => {
  const d = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
  return d.toISOString().slice(0, 10);
};
function currentYear() { return new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" })).getFullYear(); }
function currentMonth() {
  const d = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function Analytics() {
  const [dimension, setDimension] = useState("design");
  const [range, setRange] = useState("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [paymentFilter, setPaymentFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ rows: 0, units: 0, revenue: 0, refund_amount: 0, net_revenue: 0, refund_count: 0, exchange_count: 0 });
  const [loading, setLoading] = useState(false);

  const rangeParams = useMemo(() => {
    if (range === "this-year") return { year: String(currentYear()) };
    if (range === "this-month") return { month: currentMonth() };
    if (range === "custom") {
      const p = {};
      if (customFrom) p.date_from = customFrom;
      if (customTo) p.date_to = customTo;
      return p;
    }
    return {};
  }, [range, customFrom, customTo]);

  async function load() {
    setLoading(true);
    try {
      const params = { dimension, ...rangeParams };
      if (statusFilter !== "ALL") params.status = statusFilter;
      if (paymentFilter !== "ALL") params.payment_mode = paymentFilter;
      const res = await api.get("/sales/analytics", { params });
      setRows(res.data.items || []);
      setTotals(res.data.totals || {});
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [dimension, range, customFrom, customTo, statusFilter, paymentFilter]);

  const filtered = useMemo(() => {
    if (!search) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) => (r.label || "").toLowerCase().includes(q));
  }, [rows, search]);

  async function downloadExcel(dim) {
    try {
      const params = { dimension: dim, ...rangeParams };
      if (statusFilter !== "ALL") params.status = statusFilter;
      if (paymentFilter !== "ALL") params.payment_mode = paymentFilter;
      const res = await api.get("/exports/sales-analytics.xlsx", { params, responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const parts = [dim];
      if (rangeParams.month) parts.push(rangeParams.month);
      else if (rangeParams.year) parts.push(rangeParams.year);
      else if (rangeParams.date_from || rangeParams.date_to) parts.push(`${rangeParams.date_from || ""}_${rangeParams.date_to || ""}`);
      a.href = url;
      a.download = `closet-analytics-${parts.join("-")}-${todayIST()}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(`${dim.toUpperCase()} analytics exported.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  const dimMeta = DIMENSIONS.find((d) => d.id === dimension);

  // Bar width for units column visual
  const maxUnits = filtered.reduce((m, r) => Math.max(m, r.units || 0), 0) || 1;

  return (
    <div className="p-4 sm:p-6 lg:p-10" data-testid="analytics-page">
      <header className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8">
        <div>
          <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500 mb-2">/ INTEL · SALES ANALYTICS</div>
          <h1 className="section-title text-5xl lg:text-6xl">Analytics.</h1>
          <p className="text-zinc-600 mt-3 text-sm max-w-xl">See which design, size and color sells the most. Filter by month / year / custom range. Export any view as Excel.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {DIMENSIONS.map((d) => (
            <button key={d.id} onClick={() => downloadExcel(d.id)} data-testid={`download-analytics-${d.id}-btn`}
              className="brut-btn-outline px-4 py-2.5 inline-flex items-center gap-2">
              <FileDown size={14} /> {d.label} Excel
            </button>
          ))}
        </div>
      </header>

      {/* Dimension tabs */}
      <div className="flex flex-wrap gap-2 mb-4">
        {DIMENSIONS.map((d) => {
          const active = dimension === d.id;
          return (
            <button key={d.id} onClick={() => setDimension(d.id)} data-testid={`dim-tab-${d.id}`}
              className={`px-5 py-3 border-2 border-black font-display font-black text-sm uppercase tracking-wider transition-all ${
                active ? `${d.accent} text-white shadow-[4px_4px_0_0_#0a0a0a]` : "bg-white text-black hover:bg-zinc-100"
              }`}>
              {d.plural}
            </button>
          );
        })}
      </div>

      {/* Range + filters */}
      <div className="brut-card p-4 mb-5 flex flex-wrap items-center gap-3" data-testid="analytics-filters">
        <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500">Range</div>
        {RANGES.map((r) => (
          <button key={r.id} onClick={() => setRange(r.id)} data-testid={`range-tab-${r.id}`}
            className={`px-3 py-2 border-2 border-black font-mono-ds text-[11px] uppercase tracking-wider transition-all ${
              range === r.id ? "bg-black text-white shadow-[3px_3px_0_0_#0a0a0a]" : "bg-white hover:bg-zinc-100"
            }`}>{r.label}</button>
        ))}
        {range === "custom" && (
          <>
            <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="brut-input font-mono-ds max-w-[170px]" data-testid="analytics-custom-from" />
            <span className="font-mono-ds text-xs text-zinc-500">→</span>
            <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="brut-input font-mono-ds max-w-[170px]" data-testid="analytics-custom-to" />
          </>
        )}
        <div className="w-px h-8 bg-zinc-300 mx-1" />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="brut-input max-w-[180px]" data-testid="analytics-status-filter">
          {STATUSES.map((s) => <option key={s} value={s}>{s === "ALL" ? "All Statuses" : s.replace("_", " ")}</option>)}
        </select>
        <select value={paymentFilter} onChange={(e) => setPaymentFilter(e.target.value)} className="brut-input max-w-[150px]" data-testid="analytics-payment-filter">
          <option value="ALL">All Payments</option>
          <option value="PAID">Prepaid</option>
          <option value="COD">COD</option>
        </select>
        <button onClick={load} className="brut-btn-outline px-3 py-2 inline-flex items-center gap-2" data-testid="analytics-refresh"><RefreshCw size={14} /> Refresh</button>
      </div>

      {/* Top 10 Bar Chart */}
      <TopChart items={filtered} dim={dimMeta} loading={loading} />

      {/* Search + totals cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Stat label={`${dimMeta.plural} shown`} value={filtered.length} />
        <Stat label="Total Units" value={totals.units || 0} />
        <Stat label="Total Revenue" value={`₹${Math.round(totals.revenue || 0).toLocaleString("en-IN")}`} />
        <Stat label="Net Revenue" value={`₹${Math.round(totals.net_revenue || 0).toLocaleString("en-IN")}`} accent />
      </div>
      <div className="relative mb-5 max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder={`Search ${dimMeta.plural.toLowerCase()}…`}
          className="brut-input pl-10" data-testid="analytics-search" />
      </div>

      {/* Table */}
      <div className="brut-card overflow-x-auto">
        <table className="brut-table min-w-[900px]">
          <thead>
            <tr>
              <th className="w-10">#</th>
              <th>{dimMeta.label}</th>
              <th>Units</th>
              <th>Rows</th>
              <th>Revenue</th>
              <th>Refunds</th>
              <th>Refund ₹</th>
              <th>Exchanges</th>
              <th>Net Revenue</th>
              <th className="w-40">Share</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r, i) => {
              const share = maxUnits > 0 ? (r.units / maxUnits) * 100 : 0;
              return (
                <tr key={`${r.label}-${i}`} data-testid={`analytics-row-${i}`}>
                  <td className="font-mono-ds font-bold text-zinc-400">{i + 1}</td>
                  <td className="font-display font-bold max-w-[280px] truncate" title={r.label}>{r.label}</td>
                  <td className="font-mono-ds font-black text-base">{r.units}</td>
                  <td className="font-mono-ds text-xs">{r.rows}</td>
                  <td className="font-mono-ds font-bold">₹{Math.round(r.revenue).toLocaleString("en-IN")}</td>
                  <td className="font-mono-ds text-xs">{r.refund_count}</td>
                  <td className="font-mono-ds text-xs">₹{Math.round(r.refund_amount).toLocaleString("en-IN")}</td>
                  <td className="font-mono-ds text-xs">{r.exchange_count}</td>
                  <td className="font-mono-ds font-bold">₹{Math.round(r.net_revenue).toLocaleString("en-IN")}</td>
                  <td>
                    <div className="w-full h-3 border-2 border-black bg-white">
                      <div className={`${dimMeta.accent} h-full`} style={{ width: `${share}%` }} />
                    </div>
                  </td>
                </tr>
              );
            })}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={10} className="text-center py-10 text-zinc-500 font-mono-ds text-xs uppercase">
                <TrendingUp className="inline mr-2" size={14} /> No sales in this range · adjust filters
              </td></tr>
            )}
          </tbody>
          {filtered.length > 0 && (
            <tfoot>
              <tr className="bg-black text-white font-display font-black" data-testid="analytics-totals-row">
                <td></td>
                <td className="font-mono-ds text-[11px] tracking-widest uppercase">TOTAL</td>
                <td className="font-mono-ds font-black text-base">{totals.units || 0}</td>
                <td className="font-mono-ds text-xs">{totals.rows || 0}</td>
                <td className="font-mono-ds font-black text-base">₹{Math.round(totals.revenue || 0).toLocaleString("en-IN")}</td>
                <td className="font-mono-ds text-xs">{totals.refund_count || 0}</td>
                <td className="font-mono-ds text-xs">₹{Math.round(totals.refund_amount || 0).toLocaleString("en-IN")}</td>
                <td className="font-mono-ds text-xs">{totals.exchange_count || 0}</td>
                <td className="font-mono-ds font-black text-base">₹{Math.round(totals.net_revenue || 0).toLocaleString("en-IN")}</td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`brut-card p-4 ${accent ? "bg-black text-white" : ""}`}>
      <div className={`font-mono-ds text-[10px] tracking-widest uppercase mb-1 ${accent ? "opacity-70" : "text-zinc-500"}`}>{label}</div>
      <div className="font-display font-black text-2xl">{value}</div>
    </div>
  );
}

function TopChartTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-black text-white p-3 border-2 border-black shadow-[4px_4px_0_0_rgba(0,0,0,0.4)]">
      <div className="font-display font-black text-sm mb-1">{d.full}</div>
      <div className="font-mono-ds text-[10px] opacity-70 uppercase">Units: <span className="text-white opacity-100">{d.units}</span></div>
      <div className="font-mono-ds text-[10px] opacity-70 uppercase">Revenue: <span className="text-white opacity-100">₹{d.revenue.toLocaleString("en-IN")}</span></div>
      <div className="font-mono-ds text-[10px] opacity-70 uppercase">Net: <span className="text-white opacity-100">₹{d.net_revenue.toLocaleString("en-IN")}</span></div>
      {d.refund_amount > 0 && <div className="font-mono-ds text-[10px] opacity-70 uppercase text-[#FFB3B3]">Refund: <span className="opacity-100">₹{d.refund_amount.toLocaleString("en-IN")}</span></div>}
    </div>
  );
}

function TopChart({ items, dim, loading }) {
  const [metric, setMetric] = useState("units"); // units | revenue | net_revenue
  const top = useMemo(() => {
    const arr = [...items].sort((a, b) => (b[metric] || 0) - (a[metric] || 0)).slice(0, 10);
    return arr.map((r) => ({
      label: (r.label || "").length > 22 ? (r.label.slice(0, 21) + "…") : r.label,
      full: r.label,
      units: r.units,
      revenue: Math.round(r.revenue),
      net_revenue: Math.round(r.net_revenue),
      refund_amount: Math.round(r.refund_amount),
    })).reverse(); // reverse so #1 appears on top in horizontal bar chart
  }, [items, metric]);

  const height = Math.max(180, top.length * 34);

  const METRICS = [
    { id: "units",       label: "Units" },
    { id: "revenue",     label: "Revenue" },
    { id: "net_revenue", label: "Net Revenue" },
  ];

  const isMoney = metric !== "units";

  return (
    <div className="brut-card p-5 mb-4" data-testid="analytics-top-chart">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500 mb-1">Top 10 · {dim.plural}</div>
          <div className="font-display font-black text-xl">Best-selling {dim.plural.toLowerCase()} by {METRICS.find((m) => m.id === metric).label.toLowerCase()}</div>
        </div>
        <div className="flex gap-1.5">
          {METRICS.map((m) => (
            <button key={m.id} onClick={() => setMetric(m.id)} data-testid={`chart-metric-${m.id}`}
              className={`px-3 py-1.5 border-2 border-black font-mono-ds text-[10px] uppercase tracking-wider transition-all ${
                metric === m.id ? "bg-black text-white shadow-[3px_3px_0_0_#0a0a0a]" : "bg-white text-black hover:bg-zinc-100"
              }`}>{m.label}</button>
          ))}
        </div>
      </div>
      {top.length === 0 ? (
        <div className="text-center py-10 text-zinc-500 font-mono-ds text-xs uppercase">
          {loading ? "Loading…" : "No data · adjust filters"}
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={height}>
          <BarChart data={top} layout="vertical" margin={{ top: 4, right: 60, left: 8, bottom: 4 }}>
            <XAxis type="number" hide />
            <YAxis
              type="category"
              dataKey="label"
              width={170}
              tick={{ fontFamily: "JetBrains Mono, monospace", fontSize: 11, fill: "#0a0a0a" }}
              axisLine={{ stroke: "#0a0a0a", strokeWidth: 2 }}
              tickLine={false}
            />
            <Tooltip content={<TopChartTooltip />} cursor={{ fill: "rgba(0,0,0,0.06)" }} />
            <Bar dataKey={metric} radius={[0, 0, 0, 0]} stroke="#0a0a0a" strokeWidth={2}>
              {top.map((_, i) => (
                <Cell key={i} fill={i === top.length - 1 ? dim.chartAccent : dim.chartFill} />
              ))}
              <LabelList
                dataKey={metric}
                position="right"
                style={{ fontFamily: "JetBrains Mono, monospace", fontWeight: 700, fontSize: 11, fill: "#0a0a0a" }}
                formatter={(v) => (isMoney ? `₹${Number(v).toLocaleString("en-IN")}` : v)}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
